import { useEffect, useRef, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { FixtureCard } from './FixtureCard';
import { ProgressSpinner } from '@/components/ui';
import { useFixturesStore } from '@/store/fixturesStore';
import { GroupedFixtures } from '@/types/fixtures';

interface FixturesListProps {
  groupedFixtures: GroupedFixtures;
  loading: boolean;
  selectedFixtureId?: number | null;
  onToggleSelected?: (fixtureId: number) => void;
}

function formatDateHeader(dateKey: string): string {
  try {
    const date = dateKey.includes('-') ? parseISO(dateKey) : new Date(dateKey);
    return format(date, 'EEEE, dd MMM').toUpperCase();
  } catch {
    return dateKey.toUpperCase();
  }
}

export function FixturesList({
  groupedFixtures,
  loading,
  selectedFixtureId,
  onToggleSelected,
}: FixturesListProps) {
  const { hasMore, loadingMore, loadMore } = useFixturesStore();
  const observerTarget = useRef<HTMLDivElement>(null);

  // Infinite scroll implementation
  const handleLoadMore = useCallback(() => {
    if (hasMore && !loading && !loadingMore) {
      loadMore();
    }
  }, [hasMore, loading, loadingMore, loadMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px',
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [handleLoadMore]);

  const sortedDates = Object.keys(groupedFixtures).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  );

  return (
    <div className="fixtures-list">
      {sortedDates.map((date) => (
        <div key={date} className="fixture-group mb-4 last:mb-0">
          <div
            className="sticky z-10 mb-1 rounded bg-gray-100 p-2 text-lg font-semibold dark:bg-gray-700"
            style={{ top: '56px' }}
          >
            <span className="text-gray-700 dark:text-white/70">{formatDateHeader(date)}</span>
          </div>

          <div className="space-y-0.5">
            {groupedFixtures[date].map((fixture) => (
              <FixtureCard
                key={fixture.fixtureId}
                fixture={fixture}
                isSelected={fixture.fixtureId === selectedFixtureId}
                onToggleSelected={onToggleSelected}
              />
            ))}
          </div>
        </div>
      ))}

      {hasMore && (
        <div ref={observerTarget} className="flex justify-center py-6">
          {loadingMore && (
            <div className="flex items-center justify-center space-x-3 rounded-lg bg-gray-100 p-3 dark:bg-gray-700">
              <ProgressSpinner size="sm" variant="primary" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Loading more fixtures, please wait...
              </span>
            </div>
          )}
          {!loadingMore && <div className="h-4 w-full" />}
        </div>
      )}

      {!hasMore && sortedDates.length > 0 && loadingMore && (
        <div className="flex justify-center py-4">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Loading fixtures, please wait...
          </span>
        </div>
      )}
    </div>
  );
}
